import React from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { amountOf, isRecordValue, isRelativeTimeValue, relativeTimeText, RuleValue, unitOf, valueText } from './ruleAst';
import { useEntityRefOptions } from './editor/builder/entityRefOptions';
import { paramEntityRefType } from './editor/builder/valueDescriptor';
import { useRuleMetadata } from './metadata/RuleMetadataProvider';
import { enumValueLabel, paramEnumKeyPrefix, paramLabel } from './i18nKeys';

interface ComponentProps {
  params?: Record<string, unknown>;
  /** The action key, so each parameter's picker comes from the metadata (label names, not ids). */
  action?: string;
  companyId?: string;
}

/**
 * An action's parameters as a person would read them.
 *
 * The AST stores entity references by id, because that is what survives a rename — but an id is
 * unreadable, so anything with a known `entityRefType` is looked up in the same list the builder
 * picks from and shown by name. Dumping the raw JSON, as this used to, printed carrier uuids.
 */
const ActionParamsSummary: React.FC<ComponentProps> = ({ params, action, companyId }) => {
  const { actionByKey } = useRuleMetadata();
  const entries = Object.entries(params ?? {});
  const declared = actionByKey(action ?? '')?.params ?? [];
  if (entries.length === 0) return null;

  return (
    <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs text-content-secondary">
      {entries.map(([name, value]) => {
        const param = declared.find(candidate => candidate.name === name);
        return (
          <ParamEntry
            key={name}
            name={name}
            value={value as RuleValue}
            entityRefType={paramEntityRefType({ name, entityRefType: param?.entityRefType })}
            enumKeyPrefix={param && String(param.paramType) === 'ENUM' ? paramEnumKeyPrefix(action, name) ?? 'AiDispatcher.enum' : undefined}
            companyId={companyId}
          />
        );
      })}
    </span>
  );
};

const ParamEntry: React.FC<{ name: string; value: RuleValue; entityRefType?: string; enumKeyPrefix?: string; companyId?: string }> = ({
  name,
  value,
  entityRefType,
  enumKeyPrefix,
  companyId,
}) => {
  const { tr } = useTranslationLgs();
  // One lookup per parameter rather than one per action: hooks cannot run in a loop, and the
  // options themselves are cached and request-coalesced.
  const { options, supported } = useEntityRefOptions(entityRefType, companyId);

  const label = (single: RuleValue): string => {
    if (single === null || single === undefined) return '—';
    if (Array.isArray(single)) return single.map(label).join(', ');

    if (isRelativeTimeValue(single)) return relativeTimeText(single);
    // A tariff part (`unit_price(by: km, price: 25 CZK)`) reads best as the rule text writes it.
    if (isRecordValue(single)) return valueText(single);
    if (typeof single === 'object') {
      const unit = unitOf(single);
      const amount = amountOf(single);
      return unit ? `${amount} ${unit}` : String(amount ?? JSON.stringify(single));
    }

    if (typeof single === 'string' && supported) {
      const match = options.find(option => option.value === single);
      // An id with no match is shown as-is: better a raw value than a silent blank while the
      // list is still loading or the referenced row is gone.
      return match ? match.label : single;
    }

    // Only a declared ENUM goes through the enum keys: dev saves missing keys, so a free-text value
    // would push junk into Locize.
    if (typeof single === 'string' && enumKeyPrefix) return enumValueLabel(tr, single, enumKeyPrefix);

    if (typeof single === 'boolean') return single ? tr('AiDispatcher.true', 'Yes') : tr('AiDispatcher.false', 'No');
    return String(single);
  };

  return (
    <span>
      <span className="text-content-tertiary">{paramLabel(tr, name)}:</span> <span className="text-content-primary">{label(value)}</span>
    </span>
  );
};

export default ActionParamsSummary;
